import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, Chip, Alert, Collapse, Button, useTheme, alpha } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { LineChart, lineElementClasses } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import {
  GaugeChart,
  CircularProgressIndicator,
  MetricInfoIcon,
  RiskBreakdownChart,
} from '../../../../../shared/components/charts';
import { DashboardCard, CohortGuard, MetricBreakdownPanel } from '../../../../../shared/components';
import { formatTrendPercent, getRagColor, getMetricDefinition, getStrainBand } from '../../../../../shared/utils';
import { useMetricsStats } from '../../../../../shared/hooks';
import type { MetricsFilters } from '../../../../../shared/api/types';
import { useWellnessOverviewStats, useHRVTrends } from '../hooks/useWellnessOverview';
import { roundToDecimal, transformTrendsData, calculateGranularity, linearFit } from '../../shared/utils';

// RAG bands for the HRV period-over-period change: a drop of 5%+ is concerning
// (red), a rise of 5%+ is healthy (green), and anything in between is neutral
// (grey). Thresholds are in percent, applied to the plotted change series.
const HRV_CHANGE_THRESHOLDS = [-5, 5];

// Human label for the chart title, matching the fetched granularity so the
// "change" it plots is not mislabelled as daily on longer ranges.
const GRANULARITY_LABEL: Record<'hourly' | 'daily' | 'weekly' | 'monthly', string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

interface WellnessOverviewStatsColumnProps {
  filterParams: MetricsFilters;
  isCentralAdmin: boolean;
  isAnonymised: boolean;
  /** When true, the three cards stack vertically (one comparison column). */
  stacked?: boolean;
  /**
   * When true, each wellness gauge gets an expandable demographic breakdown
   * (department / age / gender / shift) scoped to this column's group.
   */
  showBreakdowns?: boolean;
  /**
   * Controlled expanded-metric state (shared across all comparison columns so
   * they expand the same metric together and stay row-aligned). When omitted,
   * the column manages its own state.
   */
  expandedMetric?: string | null;
  onToggleMetric?: (metricId: string) => void;
}

/**
 * Renders the three stats-derived cards on Wellness Overview — Average HRV gauge,
 * Overall Employee Wellness gauges, and Risk breakdown — for a single filter
 * scope. Safe to render once per comparison group (calls the stats hook once).
 */
export const WellnessOverviewStatsColumn: React.FC<WellnessOverviewStatsColumnProps> = ({
  filterParams,
  isCentralAdmin,
  isAnonymised,
  stacked = false,
  showBreakdowns = false,
  expandedMetric: controlledExpanded,
  onToggleMetric,
}) => {
  const theme = useTheme();
  // Falls back to local state when not driven by a parent (controlled props
  // keep all comparison columns expanding the same metric in lockstep).
  const [localExpanded, setLocalExpanded] = useState<string | null>(null);
  const expandedMetric = controlledExpanded !== undefined ? controlledExpanded : localExpanded;
  const toggleMetric = (metricId: string) => {
    if (onToggleMetric) onToggleMetric(metricId);
    else setLocalExpanded((prev) => (prev === metricId ? null : metricId));
  };
  const { data: statsData, isLoading: statsLoading } = useWellnessOverviewStats(filterParams);
  // Cohort count for the privacy guard, scoped to this group.
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value ?? statsData?.data?.total_active_employees?.value;

  const renderTrendBadge = (change: string | undefined) => {
    const normalized = formatTrendPercent(change) ?? '';
    if (!normalized) return null;
    const isNeg = normalized.startsWith('-');
    const isPos = normalized.startsWith('+');
    const color = isNeg ? theme.palette.error.main : isPos ? theme.palette.success.main : theme.palette.text.secondary;
    const Icon = isNeg ? TrendingDownIcon : isPos ? TrendingUpIcon : TrendingFlatIcon;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color }}>
        <Typography component='span' sx={{ fontWeight: 600, fontSize: '0.8rem', color }}>
          {normalized}
        </Typography>
        <Icon sx={{ fontSize: '0.9rem', color }} />
      </Box>
    );
  };

  const resolveRagColor = (metricId: string, value: number, apiRagColor: string | undefined): string | undefined => {
    if (apiRagColor) return getRagColor(apiRagColor, theme);
    const ragColor = getMetricDefinition(metricId)?.ragForValue?.(value);
    return ragColor ? getRagColor(ragColor, theme) : undefined;
  };

  const renderGaugeStatus = (
    metricId: string,
    ragLabel: string | undefined,
    ragColor: string | undefined,
    targetLine?: string,
  ) => (
    <Stack spacing={0.5} alignItems='center'>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {ragLabel && ragColor && (
          <Chip
            label={ragLabel}
            size='small'
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              color: ragColor,
              backgroundColor: alpha(ragColor, 0.12),
            }}
          />
        )}
        <MetricInfoIcon metricKey={metricId} />
      </Box>
      {targetLine && (
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
          {targetLine}
        </Typography>
      )}
    </Stack>
  );

  // Expandable demographic breakdown toggle shown under each gauge (average view).
  const renderBreakdownToggle = (metricId: string) => {
    if (!showBreakdowns) return null;
    const open = expandedMetric === metricId;
    return (
      <Box sx={{ pb: 2 }}>
        <Button
          size='small'
          variant='text'
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          }
          onClick={() => toggleMetric(metricId)}
          sx={{ fontSize: '0.7rem', textTransform: 'none', color: 'text.secondary' }}
        >
          {open ? 'Hide breakdown' : 'Breakdown'}
        </Button>
      </Box>
    );
  };

  const guardProps = {
    userCount: activeUserCount,
    bypass: isCentralAdmin || !isAnonymised,
    loading: statsLoading,
  };

  // HRV gauge bits
  const hrv = statsData?.data?.average_hrv ?? statsData?.data?.hrv;
  // Null (rather than 0) when the cohort recorded no HRV, so the gauge can show
  // "No data" instead of parking the needle at the bottom of the scale.
  const hrvValue = hrv?.value === null || hrv?.value === undefined ? null : roundToDecimal(hrv.value);
  const hrvBaseline = (statsData?.data?.average_hrv?.additional_fields?.baseline ||
    statsData?.data?.hrv?.additional_fields?.baseline) as { min: number; max: number } | undefined;

  // HRV change line chart (restored alongside the gauge, PEAK-137). Uses the
  // canonical calculateGranularity and the route's metrics order (['hrv',
  // 'average_hrv']) so this query's key matches the route's and React Query
  // actually dedupes it, rather than firing a second identical request.
  const hrvGranularity = calculateGranularity([
    filterParams.start_date ? dayjs(filterParams.start_date) : null,
    filterParams.end_date ? dayjs(filterParams.end_date) : null,
  ]);
  const { data: hrvTrendsData, isLoading: hrvTrendsLoading } = useHRVTrends({
    ...filterParams,
    metrics: ['hrv', 'average_hrv'],
    granularity: hrvGranularity,
  });
  const hrvUnit = hrv?.unit || 'ms';
  const hrvChangeTitle = `HRV ${GRANULARITY_LABEL[hrvGranularity]} Change`;
  // Derived series are memoised on the fetched data: this component renders once
  // per comparison column, so without this every unrelated re-render (hover,
  // expand toggle) would re-run the transform + regression for each column.
  const {
    labels: hrvChartLabels,
    rawValues: hrvChartValues,
    changeValues: hrvChangeValues,
    trendLineValues: hrvTrendLineValues,
    yBounds: hrvChangeYBounds,
    hasTrend: hasHrvTrend,
  } = useMemo(() => {
    const rows = transformTrendsData(hrvTrendsData, ['average_hrv', 'hrv']);
    const labels = rows.map((r) => String(r.label ?? ''));
    // Per row prefer average_hrv, fall back to hrv, so a response that carries
    // one key early and the other later does not collapse to nulls mid-series.
    const rawValues = rows.map((r) => {
      const raw = r.average_hrv ?? r.hrv;
      return typeof raw === 'number' ? raw : null;
    });
    // Period-over-period percent change vs the previous bucket. The plotted and
    // banded series; the first point (or any after a gap) has no prior value, so
    // it is null. A missing or near-zero baseline (sub-1ms average HRV is sensor
    // noise, not a real baseline) is skipped so it cannot explode the percentage.
    const changeValues = rawValues.map((v, i) => {
      if (i === 0 || v === null) return null;
      const prev = rawValues[i - 1];
      if (prev === null || prev < 1) return null;
      return roundToDecimal(((v - prev) / prev) * 100);
    });
    const hasTrend = changeValues.some((v) => v !== null);
    // Overall trend line: a least-squares fit through the change points, drawn
    // only across the span where data exists.
    const changePts = changeValues.map((y, x) => ({ x, y })).filter((p): p is { x: number; y: number } => p.y !== null);
    const fit = linearFit(changePts);
    const firstX = changePts[0]?.x ?? 0;
    const lastX = changePts[changePts.length - 1]?.x ?? 0;
    const trendLineValues =
      fit === null
        ? changeValues.map(() => null)
        : changeValues.map((_, i) =>
            i >= firstX && i <= lastX ? roundToDecimal(fit.slope * i + fit.intercept) : null,
          );
    // Shared y-bounds so the banded change series and the un-banded trend line
    // (on a separate axis) stay aligned.
    const nums = [...changeValues, ...trendLineValues].filter((v): v is number => typeof v === 'number');
    const yBounds =
      nums.length === 0
        ? { min: -10, max: 10 }
        : (() => {
            const lo = Math.min(...nums);
            const hi = Math.max(...nums);
            const pad = Math.max((hi - lo) * 0.1, 1);
            return { min: Math.floor(lo - pad), max: Math.ceil(hi + pad) };
          })();
    return { labels, rawValues, changeValues, trendLineValues, yBounds, hasTrend };
  }, [hrvTrendsData]);

  // Wellness gauges
  const recoveryStat = statsData?.data?.recovery ?? statsData?.data?.recovery_score;
  const strainStat = statsData?.data?.strain;
  const sleepStat = statsData?.data?.sleep_performance;
  const recoveryValue = roundToDecimal(recoveryStat?.value || 0);
  const strainValue = roundToDecimal(strainStat?.value || 0);
  const sleepValue = roundToDecimal(sleepStat?.value || 0);
  const recoveryColor = resolveRagColor('recovery_score', recoveryValue, recoveryStat?.rag_color);
  const strainBand =
    strainValue > 0 ? getStrainBand(strainValue, theme) : { label: 'Monitor', color: getRagColor('red', theme) };
  const sleepColor = resolveRagColor('sleep_performance', sleepValue, sleepStat?.rag_color);

  // Risk breakdown
  const totalActive = statsData?.data?.total_active_employees?.value || 0;
  const highRiskCount = Math.round(
    (totalActive * roundToDecimal(statsData?.data?.high_risk_employees?.value || 0)) / 100,
  );
  const mediumRiskCount = Math.round(
    (totalActive * roundToDecimal(statsData?.data?.medium_risk_employees?.value || 0)) / 100,
  );
  const lowRiskCount = Math.round(
    (totalActive * roundToDecimal(statsData?.data?.low_risk_employees?.value || 0)) / 100,
  );

  const stackDirection = stacked ? ('column' as const) : ({ xs: 'column', md: 'row' } as const);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          // CSS Grid equalises row heights: in row mode all three columns share
          // the tallest card's height; stacked (comparison) mode is a single
          // column. align-items defaults to stretch, and each DashboardCard's
          // height:100% then fills the grid cell.
          gridTemplateColumns: stacked ? '1fr' : { xs: '1fr', md: 'repeat(3, 1fr)' },
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        {/* Average HRV */}
        <DashboardCard
          title={hrv?.label || 'Average HRV'}
          subtitle='Gauge shows how current HRV compares to baseline range.'
          expandable={false}
        >
          <CohortGuard {...guardProps}>
            <GaugeChart
              value={hrvValue}
              min={0}
              max={120}
              baseline={
                hrvBaseline ? { min: roundToDecimal(hrvBaseline.min), max: roundToDecimal(hrvBaseline.max) } : undefined
              }
              unit={hrv?.unit || 'ms'}
              height={200}
              title={hrv?.label || 'Average HRV'}
              useNeutralColors
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pb: 2 }}>
              <Box>
                {showBreakdowns && (
                  <Button
                    size='small'
                    variant='text'
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: expandedMetric === 'hrv' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    }
                    onClick={() => toggleMetric('hrv')}
                    sx={{ fontSize: '0.7rem', textTransform: 'none', color: 'text.secondary' }}
                  >
                    {expandedMetric === 'hrv' ? 'Hide breakdown' : 'Breakdown'}
                  </Button>
                )}
              </Box>
              <Box>{renderTrendBadge(hrv?.change)}</Box>
            </Box>
            {/* HRV change line chart, restored alongside the gauge (PEAK-137). */}
            <Box sx={{ px: 1, pb: 1 }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                {hrvChangeTitle}
              </Typography>
              <Box sx={{ width: '100%', height: 220 }}>
                {hrvTrendsLoading ? (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant='caption' color='text.secondary'>
                      Loading HRV trend...
                    </Typography>
                  </Box>
                ) : hasHrvTrend ? (
                  <LineChart
                    height={220}
                    series={[
                      {
                        data: hrvChangeValues,
                        label: 'HRV change',
                        connectNulls: true,
                        showMark: false,
                        valueFormatter: (v, { dataIndex }) => {
                          if (v == null) return '';
                          const changeStr = `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
                          // dataIndex is only supplied in item/axis tooltip context.
                          const avg = dataIndex == null ? null : hrvChartValues[dataIndex];
                          const avgStr = typeof avg === 'number' ? ` (avg ${avg.toFixed(1)}${hrvUnit})` : '';
                          return `${changeStr}${avgStr}`;
                        },
                      },
                      {
                        id: 'hrv-trend-line',
                        data: hrvTrendLineValues,
                        label: 'Overall trend',
                        connectNulls: true,
                        showMark: false,
                        color: alpha(theme.palette.text.primary, 0.5),
                        yAxisId: 'trend',
                        valueFormatter: (v) => (v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`),
                      },
                    ]}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: hrvChartLabels,
                        tickLabelStyle: { fontSize: 11 },
                      },
                    ]}
                    yAxis={[
                      {
                        width: 50,
                        min: hrvChangeYBounds.min,
                        max: hrvChangeYBounds.max,
                        valueFormatter: (v: number) => `${v}%`,
                        colorMap: {
                          type: 'piecewise',
                          thresholds: HRV_CHANGE_THRESHOLDS,
                          colors: [theme.palette.error.main, theme.palette.text.disabled, theme.palette.success.main],
                        },
                      },
                      {
                        id: 'trend',
                        width: 0,
                        min: hrvChangeYBounds.min,
                        max: hrvChangeYBounds.max,
                        position: 'none',
                      },
                    ]}
                    margin={{ left: 0, bottom: 24 }}
                    grid={{ vertical: true, horizontal: true }}
                    sx={{
                      width: '100%',
                      [`& .${lineElementClasses.root}[data-series="hrv-trend-line"]`]: {
                        strokeDasharray: '5 5',
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant='caption' color='text.secondary'>
                      Not enough data points to plot an HRV trend for this period.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CohortGuard>
        </DashboardCard>

        {/* Overall Employee Wellness */}
        <DashboardCard
          title='Overall Employee Wellness'
          tooltip='Employee wellness metrics overview'
          expandable={false}
        >
          <CohortGuard {...guardProps}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 3,
                py: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgressIndicator
                  value={recoveryValue}
                  label='RECOVERY'
                  icon={<PersonIcon fontSize='small' />}
                  color={recoveryColor}
                  unit={recoveryStat?.unit || '%'}
                />
                {renderTrendBadge(recoveryStat?.change)}
                {renderGaugeStatus('recovery_score', recoveryStat?.rag_label, recoveryColor, 'Good ≥ 65%')}
                {renderBreakdownToggle('recovery_score')}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgressIndicator
                  value={strainValue}
                  label='STRAIN'
                  icon={<DirectionsRunIcon fontSize='small' />}
                  color={strainBand.color}
                  max={roundToDecimal((strainStat?.additional_fields?.max as number | undefined) || 21)}
                />
                {renderTrendBadge(strainStat?.change)}
                {renderGaugeStatus('strain', strainBand.label, strainBand.color)}
                {renderBreakdownToggle('strain')}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgressIndicator
                  value={sleepValue}
                  label='SLEEP'
                  icon={<BedtimeIcon fontSize='small' />}
                  color={sleepColor}
                  unit={sleepStat?.unit || '%'}
                />
                {renderTrendBadge(sleepStat?.change)}
                {renderGaugeStatus('sleep_performance', sleepStat?.rag_label, sleepColor, 'Good ≥ 75%')}
                {renderBreakdownToggle('sleep_performance')}
              </Box>
            </Box>
          </CohortGuard>
        </DashboardCard>

        {/* Risk breakdown */}
        <DashboardCard
          title='Risk breakdown'
          subtitle='Counts of active employees by risk band and participation rate.'
          tooltip='Risk score is calculated by averaging four banded risk components (Recovery, Sleep, Strain, HRV), then mapped to Low/Medium/High.'
          expandable={false}
        >
          <CohortGuard {...guardProps}>
            {totalActive > 0 ? (
              <RiskBreakdownChart
                data={{ high: highRiskCount, medium: mediumRiskCount, low: lowRiskCount }}
                total={totalActive}
                participationRate={roundToDecimal(
                  statsData?.data?.wellness_participation_rate?.value ||
                    statsData?.data?.participation_rate?.value ||
                    0,
                )}
                title={statsData?.data?.total_active_employees?.label || 'Total Active Employees'}
              />
            ) : (
              <Alert severity='info'>No risk breakdown data available.</Alert>
            )}
          </CohortGuard>
        </DashboardCard>
      </Box>

      {/* Full-width demographic breakdown — lives below the 3-card row so it has
          room to breathe and never distorts the cards' equal heights. */}
      {showBreakdowns && (
        <Collapse in={Boolean(expandedMetric)} unmountOnExit>
          {expandedMetric && (
            <DashboardCard
              title={`${getMetricDefinition(expandedMetric)?.plainName ?? expandedMetric} breakdown`}
              subtitle='By department, age, gender and shift for the current view.'
              expandable={false}
              titleComponent={
                <Button
                  size='small'
                  variant='text'
                  startIcon={<CloseIcon sx={{ fontSize: '1rem' }} />}
                  onClick={() => toggleMetric(expandedMetric)}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', color: 'text.secondary' }}
                >
                  Close
                </Button>
              }
            >
              <MetricBreakdownPanel
                metricId={expandedMetric}
                metricLabel={getMetricDefinition(expandedMetric)?.plainName ?? expandedMetric}
                unit={expandedMetric === 'strain' ? '' : expandedMetric === 'hrv' ? 'ms' : '%'}
                filterParams={filterParams}
              />
            </DashboardCard>
          )}
        </Collapse>
      )}
    </Stack>
  );
};
